import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Barbell, MagnifyingGlass, CheckCircle, Trash, Image as ImageIcon, UploadSimple, Phone } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABEL = { '1x': '1x/sem ($70)', '2x': '2x/sem ($100)' };

export default function FuncionalAdmin() {
  const [tab, setTab] = useState('avulsa'); // 'avulsa' | 'mensal'
  const [avulsas, setAvulsas] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | pago | pendente
  const [uploadingZelle, setUploadingZelle] = useState(null);
  const [sortMode, setSortMode] = useState('recent');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        axios.get(`${API}/funcional/avulsa/registrations`, { withCredentials: true }),
        axios.get(`${API}/funcional/subscriptions`, { withCredentials: true })
      ]);
      setAvulsas(a.data || []);
      setSubs(s.data || []);
    } catch (e) {
      toast.error('Erro ao carregar dados do Funcional');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isAvulsa = tab === 'avulsa';
  const baseList = isAvulsa ? avulsas : subs;
  const basePath = isAvulsa ? 'funcional/avulsa/registrations' : 'funcional/subscriptions';
  const zelleUploadPath = isAvulsa ? 'funcional/avulsa/zelle-receipt?registration_id=' : 'funcional/subscription/zelle-receipt?subscription_id=';
  const receiptViewPath = isAvulsa ? 'funcional/avulsa/zelle-receipt' : 'funcional/subscriptions/zelle-receipt';

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este registro?')) return;
    try {
      await axios.delete(`${API}/${basePath}/${id}`, { withCredentials: true });
      toast.success('Registro removido');
      fetchData();
    } catch (e) {
      toast.error('Erro ao remover');
    }
  };

  const handleStatusUpdate = async (id, status, paymentStatus) => {
    try {
      await axios.put(`${API}/${basePath}/${id}`, { status, payment_status: paymentStatus, payment_method: 'manual' }, { withCredentials: true });
      toast.success('Pagamento confirmado');
      fetchData();
    } catch (e) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleZelleUpload = async (id, file) => {
    if (!file) return;
    setUploadingZelle(id);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/${zelleUploadPath}${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      toast.success('Comprovante enviado! Pagamento confirmado.');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar comprovante');
    } finally {
      setUploadingZelle(null);
    }
  };

  const viewReceipt = (id) => {
    const token = localStorage.getItem('access_token');
    window.open(`${API}/${receiptViewPath}/${id}?auth=${token}`, '_blank');
  };

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return baseList.filter((r) => {
      if (statusFilter !== 'all' && (r.payment_status || 'pendente') !== statusFilter) return false;
      if (!term) return true;
      return (
        (r.name || '').toLowerCase().includes(term) ||
        (r.phone || '').includes(search) ||
        (r.email || '').toLowerCase().includes(term) ||
        (r.preferred_date || '').includes(search) ||
        (r.reference_month || '').includes(search)
      );
    });
  }, [baseList, search, statusFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      if (sortMode === 'name-asc') return (a.name || '').localeCompare(b.name || '', 'pt-BR');
      if (sortMode === 'name-desc') return (b.name || '').localeCompare(a.name || '', 'pt-BR');
      return (b.created_at || '').localeCompare(a.created_at || '');
    });
    return arr;
  }, [filtered, sortMode]);

  const total = baseList.length;
  const paidCount = baseList.filter((r) => r.payment_status === 'pago').length;
  const pendingCount = baseList.filter((r) => (r.payment_status || 'pendente') === 'pendente').length;
  const revenue = baseList
    .filter((r) => r.payment_status === 'pago')
    .reduce((sum, r) => sum + (Number(r.amount) || (isAvulsa ? 30 : 0)), 0);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="funcional-admin-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Treinamento Funcional</h1>
            <p className="text-[#73706A] mt-1">Ter/Qui 07AM &middot; Avulsa $30 &middot; Mensal 1x $70 / 2x $100</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-[#F5F2EB] rounded-lg p-1 w-fit" data-testid="funcional-admin-tabs">
          <button
            onClick={() => { setTab('avulsa'); setStatusFilter('all'); setSearch(''); }}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${tab === 'avulsa' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`}
            data-testid="funcional-tab-avulsa"
          >
            Avulsas ($30)
          </button>
          <button
            onClick={() => { setTab('mensal'); setStatusFilter('all'); setSearch(''); }}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${tab === 'mensal' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`}
            data-testid="funcional-tab-mensal"
          >
            Mensalidades
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <button
            onClick={() => setStatusFilter('all')}
            className={`stat-card text-left transition-all ${statusFilter === 'all' ? 'ring-2 ring-[#E85D04]' : ''}`}
            data-testid="funcional-stat-total"
          >
            <p className="text-sm text-[#73706A]">Total</p>
            <p className="text-2xl font-semibold text-[#1A1A1A] font-['Outfit']">{total}</p>
          </button>
          <button
            onClick={() => setStatusFilter('pago')}
            className={`stat-card text-left transition-all ${statusFilter === 'pago' ? 'ring-2 ring-[#2D6A4F]' : ''}`}
            data-testid="funcional-stat-paid"
          >
            <p className="text-sm text-[#73706A]">Pagos</p>
            <p className="text-2xl font-semibold text-[#2D6A4F] font-['Outfit']">{paidCount}</p>
          </button>
          <button
            onClick={() => setStatusFilter('pendente')}
            className={`stat-card text-left transition-all ${statusFilter === 'pendente' ? 'ring-2 ring-[#E85D04]' : ''}`}
            data-testid="funcional-stat-pending"
          >
            <p className="text-sm text-[#73706A]">Pendentes</p>
            <p className="text-2xl font-semibold text-[#E85D04] font-['Outfit']">{pendingCount}</p>
          </button>
          <div className="stat-card" data-testid="funcional-stat-revenue">
            <p className="text-sm text-[#73706A]">Receita</p>
            <p className="text-2xl font-semibold text-[#2D6A4F] font-['Outfit']">${revenue.toFixed(2)}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1 min-w-[220px]">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#73706A]" />
            <Input
              placeholder={isAvulsa ? 'Buscar nome, telefone, e-mail ou data...' : 'Buscar nome, telefone, e-mail ou mês...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-funcional"
            />
          </div>
          <div className="flex items-center gap-1 bg-[#F5F2EB] rounded-lg p-1" data-testid="funcional-sort-controls">
            <button onClick={() => setSortMode('recent')} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'recent' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`} data-testid="funcional-sort-recent">Recentes</button>
            <button onClick={() => setSortMode('name-asc')} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'name-asc' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`} data-testid="funcional-sort-asc">A-Z</button>
            <button onClick={() => setSortMode('name-desc')} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'name-desc' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`} data-testid="funcional-sort-desc">Z-A</button>
          </div>
        </div>

        <div className="bg-white border border-[#E6E2D8] rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : sorted.length === 0 ? (
            <div className="p-8 text-center text-[#73706A]" data-testid="funcional-empty">
              {search || statusFilter !== 'all' ? 'Nenhum registro encontrado' : (isAvulsa ? 'Nenhuma aula avulsa registrada ainda' : 'Nenhuma assinatura registrada ainda')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F5F2EB]">
                    <th className="table-header">Nome</th>
                    <th className="table-header">Telefone</th>
                    {isAvulsa ? (
                      <th className="table-header">Data</th>
                    ) : (
                      <>
                        <th className="table-header">Plano</th>
                        <th className="table-header">Mês Ref.</th>
                      </>
                    )}
                    <th className="table-header">Valor</th>
                    <th className="table-header">Pagamento</th>
                    <th className="table-header">Status</th>
                    <th className="table-header text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((reg, index) => (
                    <tr key={reg.id} className="hover:bg-[#F5F2EB]/50 transition-colors" data-testid={`funcional-row-${index}`}>
                      <td className="table-cell">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#E85D04] rounded-full flex items-center justify-center">
                            <Barbell size={18} className="text-white" weight="fill" />
                          </div>
                          <div className="flex flex-col">
                            <span className="font-medium text-[#1A1A1A]">{reg.name}</span>
                            {reg.email && <span className="text-xs text-[#73706A]">{reg.email}</span>}
                          </div>
                        </div>
                      </td>
                      <td className="table-cell text-[#1A1A1A]">{reg.phone}</td>
                      {isAvulsa ? (
                        <td className="table-cell text-[#1A1A1A]">{reg.preferred_date || '-'}</td>
                      ) : (
                        <>
                          <td className="table-cell text-[#1A1A1A]">{PLAN_LABEL[reg.plan] || reg.plan || '-'}</td>
                          <td className="table-cell text-[#1A1A1A]">{reg.reference_month || '-'}</td>
                        </>
                      )}
                      <td className="table-cell font-medium text-[#2D6A4F]">$ {(Number(reg.amount) || (isAvulsa ? 30 : 0)).toFixed(2)}</td>
                      <td className="table-cell">
                        <span className={`badge ${reg.payment_status === 'pago' ? 'badge-success' : 'badge-warning'}`}>
                          {reg.payment_status || 'pendente'}
                        </span>
                        {reg.payment_method && (
                          <span className="text-xs text-[#73706A] ml-1">({reg.payment_method})</span>
                        )}
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${reg.status === 'confirmada' ? 'badge-success' : 'badge-warning'}`}>
                          {reg.status || 'pendente'}
                        </span>
                      </td>
                      <td className="table-cell text-right">
                        <div className="flex items-center justify-end gap-1">
                          {reg.payment_status !== 'pago' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStatusUpdate(reg.id, 'confirmada', 'pago')}
                                className="text-[#2D6A4F] hover:text-[#1B4332] hover:bg-green-50"
                                data-testid={`confirm-funcional-${index}`}
                                title="Confirmar pagamento manualmente"
                              >
                                <CheckCircle size={18} />
                              </Button>
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                                  className="hidden"
                                  onChange={(e) => handleZelleUpload(reg.id, e.target.files[0])}
                                  data-testid={`funcional-zelle-upload-${index}`}
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-[#0077B6] hover:text-[#03045E] hover:bg-blue-50 pointer-events-none"
                                  disabled={uploadingZelle === reg.id}
                                  title="Enviar comprovante Zelle"
                                >
                                  {uploadingZelle === reg.id ? (
                                    <div className="w-4 h-4 border-2 border-[#0077B6] border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <UploadSimple size={18} />
                                  )}
                                </Button>
                              </label>
                            </>
                          )}
                          {reg.zelle_receipt_path && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewReceipt(reg.id)}
                              className="text-[#0077B6] hover:text-[#03045E] hover:bg-blue-50"
                              data-testid={`view-funcional-receipt-${index}`}
                              title="Ver comprovante Zelle"
                            >
                              <ImageIcon size={18} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(reg.id)}
                            className="text-[#9B2226] hover:text-[#9B2226] hover:bg-red-50"
                            data-testid={`delete-funcional-${index}`}
                          >
                            <Trash size={18} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <Phone size={24} className="text-[#0077B6]" />
            <h3 className="font-['Outfit'] font-medium text-lg text-[#1A1A1A]">Pagamento Zelle</h3>
          </div>
          <p className="text-sm text-[#73706A]">
            Clique no ícone <UploadSimple size={14} className="inline text-[#0077B6]" /> para enviar o comprovante Zelle do aluno. O sistema confirma o pagamento automaticamente.
          </p>
        </div>
      </div>
    </Layout>
  );
}
